import { logger } from '@adornis/base/logging.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { AudioRecorder } from '@adornis/recorder/client/recorder.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { hasRole } from '@ils/permissions/hasRole.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { UserRole } from '@ils/static/enums.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { type RemoteParticipant, type RemoteTrack, type RemoteTrackPublication, Track } from 'livekit-client';
import type { ILSUser } from '../../../db/ils-user.js';
import { answerUploader, blobStore } from '../../global-state.js';
import { ILSVideoChat } from './ils-video-chat.js';

@customElement('ils-exam-video-chat')
export class ILSExamVideoChat extends ILSVideoChat {
  @state() recording = false;
  @state() recordEnabled = false;
  @state() currentSlot?: Slot;
  @state() protected _currentUser = new RXController(this, currentUser<ILSUser>());

  combinedRecorder = new AudioRecorder({ store: blobStore });
  localRecorder = new AudioRecorder({ store: blobStore });

  enableRecording() {
    this.recordEnabled = true;
    // if we do not record but already have all tracks start right away
    if (this.combinedRecorder.hasStreamA && this.combinedRecorder.hasStreamB && this.currentSlot) {
      this.combinedRecorder.startRecording(this.currentSlot._id + '_combined');
      this.localRecorder.startRecording(this.currentSlot._id + '_trainee');
      this.recording = true;
    }
  }

  stopRecording(slot: Slot) {
    if (!this.recordEnabled) return;
    this.combinedRecorder.stopRecording();
    this.localRecorder.stopRecording();
    this.recordEnabled = false;
    this.recording = false;
    this.combinedRecorder.once('file', (event: File[]) =>
      answerUploader
        .saveAnswerForSlot(slot, 'combined', event)
        .catch(error => logger.error(error, 'error while uploading combined file')),
    );
    this.localRecorder.once('file', (event: File[]) =>
      answerUploader
        .saveAnswerForSlot(slot, 'trainee', event)
        .catch(error => logger.error(error, 'error while uploading trainee file')),
    );
    // remove remote peers audio stream, so we dont start recording too early after switching slots
    this.combinedRecorder.removeStreamA();
  }

  // willUpdate is used to auto join the roomID on the slot once its available
  protected override async willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    if (
      _changedProperties.has('currentSlot') &&
      this.currentSlot &&
      _changedProperties.get('currentSlot')?._id !== this.currentSlot._id
    ) {
      await this._joinRoom(this.currentSlot.roomID);
    }
  }

  protected override ontrack(track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) {
    // make sure incoming video gets rendered in UI
    // requestAnimationFrame(() => this.requestUpdate());

    const currentSlot = this.currentSlot;
    const currentUser = this._currentUser.value;
    const localStream = this.livekitController.getLocalStream();

    if (!currentSlot) throw new Error('no currentSlot in onstream?'); // should not happen because we get the room-id from the slot
    if (!currentUser) throw new Error('no currentUser in onstream?');
    if (!localStream) throw new Error('no localStream in onstream?');
    const otherUser = currentUser._id === currentSlot.candidateID ? currentSlot.tester! : currentSlot.candidateID!;
    if (participant.identity === otherUser) {
      this.livekitController.sendData(this.textEncoder.encode('connected')); // sending 'connected' message to trigger state receiving
      if (track.source === Track.Source.Microphone)
        this.combinedRecorder.replaceStreamA(new MediaStream([track.mediaStreamTrack]));
      this.combinedRecorder.replaceStreamB(localStream);
      this.localRecorder.replaceStreamA(localStream);
      if (!this.recording && this.recordEnabled) {
        this.combinedRecorder.startRecording(currentSlot._id + '_combined');
        this.localRecorder.startRecording(currentSlot._id + '_trainee');
        this.recording = true;
      }
    }
  }

  // eslint-disable-next-line complexity
  override statusDisplayWhenReady() {
    return html`
      ${this.currentSlot && hasRole(this._currentUser.value, UserRole.TRAINEE) && this.currentSlot.recordAudio
        ? html`
            <x-basic-card padding="md" ${css({ position: 'absolute', top: '1em', left: '1em' })}>
              <x-flex horizontal crossaxis-center space="sm">
                <x-icon
                  ${css({
                    color: this.recording ? 'red' : this.colors.secondary,
                  })}
                >
                  circle
                </x-icon>
                <x-text> Aufnahme ${this.recording ? 'läuft' : 'gestoppt'} </x-text>
              </x-flex>
            </x-basic-card>
          `
        : nothing}
      ${this.livekitController.getLocalTrackPublication(Track.Source.Camera)?.isMuted === false
        ? nothing
        : html`
            <x-flex
              ${css({
                position: 'absolute',
                bottom: '1em',
                right: '1em',
                width: '10vmin',
                height: '10vmin',
                zIndex: '10',
                background: 'white',
                borderRadius: this.sizes.borderRadius,
              })}
              center
              crossaxis-center
            >
              <x-icon ${css({ fontSize: '2em' })}> call </x-icon>
            </x-flex>
          `}
    `;
  }
}
